import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ALL_MODULE_KEYS, type ModuleKey } from '../../constants/modules';

interface Props {
  currentPage: string;
  onNavigate: (page: string) => void;
  user: any;
  onLogout: () => void;
  onThemeToggle: () => void;
  isDark: boolean;
}

export default function Sidebar({ currentPage, onNavigate, user, onLogout, onThemeToggle, isDark }: Props) {
  const { t, i18n } = useTranslation();
  const [monitoringOpen, setMonitoringOpen] = useState(true);
  const role = user?.role ?? '';
  const isSuperAdmin = role === 'super_admin';
  const isAdminOrUp  = isSuperAdmin || role === 'tenant_admin';
  // Mientras el WS no responda con `connected`, todo visible por defecto —
  // ver App.tsx. Gestión de clientes/dispositivos vive en admin.dimasfarming.com
  // (apps/web/src/admin/), ya no en este Sidebar.
  const enabled = new Set<ModuleKey>(user?.enabledModules ?? ALL_MODULE_KEYS);

  const toggleLanguage = () => {
    i18n.changeLanguage(i18n.language.startsWith('es') ? 'en' : 'es');
  };

  const monitoringActive = ['soil', 'weather', 'pest', 'battery'].includes(currentPage);
  const anyMonitoringEnabled = enabled.has('soil') || enabled.has('weather') || enabled.has('pest') || enabled.has('battery');

  return (
    <div className="rail" style={{ width: 220, background: 'var(--white)', borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
      {/* Header */}
      <div style={{ padding: '16px 14px 12px', borderBottom: '1px solid var(--border)' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--dark)', letterSpacing: '0.1em' }}>DIMAS FARMING</div>
        <div style={{ fontSize: 9, color: 'var(--text-muted)', marginTop: 2 }}>{t('sidebar.platform')}</div>
      </div>

      {/* User */}
      {user && (
        <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 9 }}>
          <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--dark)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600, color: 'var(--green)', flexShrink: 0 }}>
            {user.email?.[0]?.toUpperCase() || 'U'}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--dark)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user.name || user.email || 'Usuario'}
            </div>
            <div style={{ fontSize: 9, color: 'var(--text-muted)' }}>
              {String(t(`sidebar.roles.${role}`, role))}
            </div>
          </div>
        </div>
      )}

      {/* Nav */}
      <div style={{ flex: 1, padding: '8px 6px', overflowY: 'auto' }}>

        {/* Dashboard — standalone, no section label */}
        <NavItem icon="🌾" label={t('sidebar.nav.dashboard')} active={currentPage === 'home'} onClick={() => onNavigate('home')} />

        {/* Monitoring — collapsible submenu; oculto si el tenant no tiene ningún módulo de monitoreo habilitado */}
        {anyMonitoringEnabled && (
          <CollapsibleSection
            label={t('sidebar.sections.monitoring')}
            open={monitoringOpen}
            hasActiveChild={monitoringActive}
            onToggle={() => setMonitoringOpen(o => !o)}
          >
            {enabled.has('soil')    && <NavItem icon="🌱" label={t('sidebar.nav.soil')}    active={currentPage === 'soil'}    onClick={() => onNavigate('soil')}    indent />}
            {enabled.has('weather') && <NavItem icon="🌤" label={t('sidebar.nav.weather')} active={currentPage === 'weather'} onClick={() => onNavigate('weather')} indent />}
            {enabled.has('pest')    && <NavItem icon="🐛" label={t('sidebar.nav.pest')}    active={currentPage === 'pest'}    onClick={() => onNavigate('pest')}    indent />}
            {enabled.has('battery') && <NavItem icon="🔋" label={t('sidebar.nav.battery')} active={currentPage === 'battery'} onClick={() => onNavigate('battery')} indent />}
          </CollapsibleSection>
        )}

        <SectionLabel>{t('sidebar.sections.operations')}</SectionLabel>
        {enabled.has('locations')  && <NavItem icon="📍" label={t('sidebar.nav.locations')} active={currentPage === 'locations'}  onClick={() => onNavigate('locations')} />}
        {enabled.has('alerts')     && <NavItem icon="🔔" label={t('sidebar.nav.alerts')} active={currentPage === 'alerts'}     onClick={() => onNavigate('alerts')} />}
        {enabled.has('thresholds') && <NavItem icon="📊" label={t('sidebar.nav.thresholds')} active={currentPage === 'thresholds'} onClick={() => onNavigate('thresholds')} />}
        {enabled.has('reports')    && <NavItem icon="📋" label={t('sidebar.nav.reports')} active={currentPage === 'reports'}    onClick={() => onNavigate('reports')} />}
        {enabled.has('mapas')      && <NavItem icon="🗺" label={t('sidebar.nav.mapas')} active={currentPage === 'mapas'} onClick={() => onNavigate('mapas')} />}
        <NavItem icon="⚙️" label={t('sidebar.nav.settings')}  active={currentPage === 'settings'}   onClick={() => onNavigate('settings')} />

        {isAdminOrUp && (
          <>
            <SectionLabel>{t('sidebar.sections.admin')}</SectionLabel>
            <NavItem icon="👥" label={t('sidebar.nav.users')} active={currentPage === 'users'} onClick={() => onNavigate('users')} />
          </>
        )}
      </div>

      {/* Footer */}
      <div style={{ padding: '12px 14px', borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            onClick={onThemeToggle}
            style={{ flex: 1, padding: '7px 8px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 6, fontSize: 11, color: 'var(--gray)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <span>{isDark ? '☀️' : '🌙'}</span>
            <span>{isDark ? t('sidebar.lightMode') : t('sidebar.darkMode')}</span>
          </button>
          <button
            onClick={toggleLanguage}
            title={i18n.language.startsWith('es') ? 'Switch to English' : 'Cambiar a Español'}
            style={{ padding: '7px 10px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 6, fontSize: 11, fontWeight: 600, color: 'var(--gray)', cursor: 'pointer', flexShrink: 0 }}
          >
            {t('sidebar.langToggle')}
          </button>
        </div>
        <button
          onClick={onLogout}
          style={{ width: '100%', padding: '7px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, color: 'var(--gray)', cursor: 'pointer', transition: 'all 0.2s' }}
          onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
        >
          {t('sidebar.logout')}
        </button>
      </div>
    </div>
  );
}

function CollapsibleSection({
  label, open, hasActiveChild, onToggle, children,
}: {
  label: string;
  open: boolean;
  hasActiveChild: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div
        onClick={onToggle}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '9px 8px 3px', cursor: 'pointer', userSelect: 'none',
        }}
      >
        <span style={{
          fontSize: 9, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase',
          color: hasActiveChild ? 'var(--green-dk)' : 'var(--text-muted)',
        }}>
          {label}
        </span>
        <span style={{
          fontSize: 9, color: 'var(--text-muted)',
          transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
          transition: 'transform 0.15s',
          display: 'inline-block',
        }}>
          ▾
        </span>
      </div>
      {open && <div>{children}</div>}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 9, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase', padding: '9px 8px 3px' }}>
      {children}
    </div>
  );
}

function NavItem({ icon, label, active, onClick, indent = false, badge }: {
  icon: string; label: string; active: boolean; onClick: () => void; indent?: boolean; badge?: number;
}) {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 9,
        padding: `8px 10px 8px ${indent ? 18 : 10}px`,
        borderRadius: 7, cursor: 'pointer', transition: 'background 0.12s', marginBottom: 1,
        background: active ? 'var(--green-bg)' : 'transparent',
      }}
      onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'var(--bg)'; }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
    >
      <span style={{ fontSize: 15, width: 18, textAlign: 'center' }}>{icon}</span>
      <span style={{ fontSize: 12, color: active ? 'var(--green-dk)' : 'var(--dark)', fontWeight: active ? 500 : 400, flex: 1 }}>{label}</span>
      {!!badge && (
        <span style={{
          fontSize: 10, fontWeight: 700, color: '#fff', background: 'var(--red)',
          borderRadius: 99, minWidth: 16, height: 16, padding: '0 5px',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          {badge}
        </span>
      )}
    </div>
  );
}
